import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import {
  PARTNER_BANK_JOURNEY_STAGES,
  PartnerBankJourneyCardResponse,
  PartnerBankJourneyStage,
  partnerBankJourneyStageLabel,
} from '@interfaces/partners/responses/partner-bank-journey.response';
import {
  BANK_JOURNEY_STAGE_TONES,
  bankJourneyStageOrder,
  bankJourneyTotalsByCurrency,
} from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/bank-journey-totals';
import { KanbanBoardComponent } from '@shared/components/kanban-board/kanban-board.component';
import {
  KanbanBoardColumn,
  KanbanColumnTotal,
} from '@shared/components/kanban-board/kanban-board.types';
import { SharedModule } from '@src/app/shared/shared.module';
import { DealBankJourneyStore } from '@features/dashboard/commercial/pages/deal-detail/components/bank-journey/deal-bank-journey.store';

/** Coluna da jornada: o id é a etapa do parceiro. */
interface JourneyColumn extends KanbanBoardColumn {
  id: PartnerBankJourneyStage;
}

/** Cartão no formato que o board pede (`id`). */
type JourneyCard = PartnerBankJourneyCardResponse & { id: string };

/**
 * Aba **Bancos** da tela do deal (card 3707; até ali era o segundo quadro da tela Meus deals, card 3612).
 *
 * Mostra cada banco do deal (negociação do pipe de crédito ligada a ele) na etapa que o parceiro entende:
 * Apresentação, Envio de documentação, Análise do crédito, Rejeitado, Aprovado e Desembolso. Quem traduz a etapa do
 * pipe para a do parceiro é o backend (`GET partners/deals/{id}/bank-journey`); aqui só se desenha.
 *
 * - O dado vem do `DealBankJourneyStore` da tela, o mesmo do cartão da Visão geral; a tela só mostra a aba com
 *   `unlocked`.
 * - Ninguém arrasta: a etapa do banco muda no pipe de crédito, pelo time interno.
 * - Clicar no cartão abre os comentários (o painel é da tela, `app-bank-journey-comments-drawer`).
 * - Card 3752: deixou de ser uma aba entre as do deal e virou o conteúdo da visão **Jornada nos bancos** do
 *   cabeçalho (`app-deal-journey-switch`). O título e a contagem subiram para o resumo do cabeçalho
 *   (`app-deal-bank-journey-summary`); aqui ficam duas vistas, **Quadro** (o kanban) e **Lista** (uma tabela
 *   ordenada pela etapa). A escolha da vista não vai para a URL: o link aponta para os bancos, não para a vista.
 * - Card 3765: também é a visão **Jornada nos bancos** da tela Meus deals, com os bancos de todos os deals
 *   (`showDeal`): o cartão e a lista ganham a empresa e o número do deal, com link para ele.
 */
@Component({
  selector: 'app-deal-bank-journey-tab',
  standalone: true,
  imports: [CommonModule, RouterLink, SharedModule, KanbanBoardComponent],
  templateUrl: './deal-bank-journey-tab.component.html',
  styleUrl: './deal-bank-journey-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealBankJourneyTabComponent {
  protected readonly store = inject(DealBankJourneyStore);

  /** Bancos de vários deals (Meus deals, card 3765): mostra a empresa e o número do deal em cada banco. */
  readonly showDeal = input(false);

  protected readonly columns: JourneyColumn[] = PARTNER_BANK_JOURNEY_STAGES.map(stage => ({
    id: stage.value,
    name: stage.label,
  }));
  protected readonly minimizedColumnIds = signal<ReadonlySet<string>>(new Set());
  protected readonly summaryMinimized = signal(false);

  protected readonly view = signal<'board' | 'list'>('board');
  protected readonly tones = BANK_JOURNEY_STAGE_TONES;
  protected readonly stageLabel = partnerBankJourneyStageLabel;

  /**
   * Dica ao lado das vistas. Card 3848: só o time interno comenta (o servidor manda `canComment`), então o parceiro
   * lê "ver os comentários".
   */
  protected readonly hint = computed(() =>
    this.store.cards().some(card => card.canComment)
      ? 'Clique num banco para ver e escrever comentários'
      : 'Clique num banco para ver os comentários'
  );

  protected readonly cards = computed<JourneyCard[]>(() =>
    this.store.cards().map(card => ({ ...card, id: card.pipelineDealId }))
  );

  /** Lista: na ordem das colunas do quadro e, dentro da etapa, pelo nome do banco. */
  protected readonly sortedCards = computed(() =>
    [...this.store.cards()].sort(
      (a, b) =>
        bankJourneyStageOrder(a.stage) - bankJourneyStageOrder(b.stage) ||
        (a.bankName ?? '').localeCompare(b.bankName ?? '') ||
        a.companyName.localeCompare(b.companyName)
    )
  );

  // Adaptadores do board. Arrow functions de propósito: o board chama sem o `this` daqui.
  protected readonly cardsOf = (column: JourneyColumn): JourneyCard[] =>
    this.cards().filter(card => card.stage === column.id);
  protected readonly countOf = (column: JourneyColumn): number => this.cardsOf(column).length;
  protected readonly isColumnEmpty = (column: JourneyColumn): boolean =>
    this.cardsOf(column).length === 0;
  /** Uma linha por moeda, como no quadro do deal; BRL primeiro. */
  protected readonly totalsOf = (column: JourneyColumn): KanbanColumnTotal[] => {
    const totals = bankJourneyTotalsByCurrency(this.cardsOf(column));
    return totals.length ? totals : [{ currency: 'BRL', count: 0, value: 0 }];
  };

  protected toggleColumn(columnId: string): void {
    this.minimizedColumnIds.update(current => {
      const next = new Set(current);
      if (!next.delete(columnId)) next.add(columnId);
      return next;
    });
  }
}
